import { html, nothing, type TemplateResult } from "lit";
import type { PublicCharacteristics } from "../protocol";
import type { Frame } from "./field";
import { renderManaText } from "./mana-pips";

// Arena's "Choose one": the table dims, and the options lie large across the middle under the
// question and what to do, with the way out beneath them. The dimmed table still takes a press,
// which cancels as a press outside any popup does.
export type ChooseOne = {
  // The dialog's accessible name.
  label: string;
  title: string;
  sub: string;
  // The dialog's own classes besides choose-one's: card-menu for a menu the viewer opened rather
  // than a question the game asks.
  classes?: string;
  style?: string;
  optionsClass?: string;
  options: TemplateResult[];
  aside?: TemplateResult;
  hide?: () => void;
  foot: TemplateResult;
};

export function renderChooseOne(c: ChooseOne): TemplateResult {
  return html`<div class="pop prompt choose-one ${c.classes ?? ""}" role="dialog" aria-label=${c.label} style=${c.style ?? nothing}>
    <header class="choose-heading">
      <h3>${c.title}</h3>
      <div class="sub">${c.sub}</div>
    </header>
    <div class="choose-options choices ${c.optionsClass ?? ""}">${c.options}${c.aside ? html`<div class="choose-aside">${c.aside}</div>` : nothing}</div>
    <div class="choose-actions">${c.foot}${c.hide ? html`<button class="choose-hide" title="Hide this to look at the board; the big button brings it back" @click=${c.hide}>View battlefield</button>` : nothing}</div>
  </div>`;
}

// An option as a card, lit as something to pick, named by `label` and with what picking it does
// beneath it, which needn't say what the card already does.
export function renderOption(
  picture: TemplateResult,
  label: string,
  caption: string,
  pick: () => void,
  hover?: (e: MouseEvent | null) => void,
  disabled = false,
  title?: string,
): TemplateResult {
  return html`<button class="option${disabled ? " unavailable" : ""}" aria-label=${label} title=${title ?? nothing} ?disabled=${disabled} @click=${pick}
      @mouseenter=${(e: MouseEvent) => hover?.(e)} @mouseleave=${() => hover?.(null)}>
    <span class="option-card">${picture}</span>
    ${caption === "" ? nothing : html`<span class="option-how">${caption}</span>`}
  </button>`;
}

// An ability as Arena shows it, as a card of its own: in its source's frame, with the source's art
// and name, what it is ("Ability", "Trigger"), and its text, a loyalty ability's cost beside it.
export function renderAbilityCard(source: string, kind: string, text: string, frame: Frame, artKey = ""): TemplateResult {
  const parts = kind === "Ability" ? splitLoyaltyCost(text) : { text, loyalty: null };
  return html`<card-art name=${source} artKey=${artKey} ability=${kind} frame=${frame} oracleText=${parts.text} loyalty=${parts.loyalty ?? ""}></card-art>`;
}

// A loyalty ability's cost as its Oracle line starts: "[+1]: ", "[−X]: " or "[0]: ", with or without
// the brackets.
export function splitLoyaltyCost(line: string): { text: string; loyalty: string | null } {
  const match = line.match(/^\[?([+−-](?:\d+|X)|0)\]?:\s*/);
  return { text: match ? line.slice(match[0].length) : line, loyalty: match?.[1] ?? null };
}

export function renderChoiceList(choices: string[], pick: (i: number) => void): TemplateResult {
  return html`<div class="choice-list">${choices.map((text, i) => html`<button class="choice-row" aria-label=${text} @click=${() => pick(i)}>
      <span class="choice-text">${renderManaText(text)}</span>${i < 9 ? html`<kbd>${i + 1}</kbd>` : nothing}
    </button>`)}</div>`;
}

// The line of a card's Oracle text an ability is: the line the engine wrote its row from
// (ActivatedAbility.text, Trigger.text; the ability-text sweep checks every row has one), or for
// cycling, which is no row of its own, its cycling line. Reminder text is left out of a cycling line.
export function abilityLine(oracleText: string, face: PublicCharacteristics | null, kind: "activated" | "triggered" | "cycling", n: number): string | null {
  if (kind === "cycling") return oracleText.split("\n").map(withoutReminder).find((l) => /cycling\b/i.test(l)) ?? null;
  return abilityLines(face, kind)[n] ?? null;
}

// The activated abilities' (or triggers') lines, in their index order.
export function abilityLines(face: PublicCharacteristics | null, kind: "activated" | "triggered" = "activated"): (string | null)[] {
  const texts = kind === "triggered" ? face?.trigger_texts : face?.activated_texts;
  return (texts ?? []).map((t) => t.kind === "Some" ? t.values[0] : null);
}

function withoutReminder(line: string): string {
  return line.replace(/\s*\([^)]*\)/g, "").trim();
}
